import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class IsEqualWithService {
  /**
   * Checks if two values are equal using a customizer function
   * @param value The first value to compare
   * @param other The second value to compare
   * @param customizer The customizer function to use for comparison
   * @returns true if the values are equal, false otherwise
   */
  static isEqualWith(
    value: any,
    other: any,
    customizer?: (
      objValue: any,
      othValue: any,
      indexOrKey?: string | number,
      object?: any,
      othObject?: any,
      stack?: any,
    ) => boolean | undefined,
  ): boolean {
    function compareWith(
      value: any,
      other: any,
      customizer?: (
        objValue: any,
        othValue: any,
        indexOrKey?: string | number,
        object?: any,
        othObject?: any,
        stack?: any,
      ) => boolean | undefined,
      parentValue?: any,
      parentOther?: any,
      key?: string | number,
      stack: Set<any> = new Set(),
    ): boolean {
      // Prevent circular references
      if (stack.has(value) || stack.has(other)) {
        return value === other;
      }

      // If customizer is provided, call it with all available arguments
      if (customizer) {
        const result = customizer(value, other, key, parentValue, parentOther, stack);
        if (typeof result === 'boolean') return result;
      }

      // Handle null and undefined
      if (value === other) return true;
      if (value === null || other === null || value === undefined || other === undefined)
        return false;

      // Handle primitive types
      if (typeof value !== 'object' && typeof other !== 'object') return value === other;

      // Add objects to stack before recursive comparison
      if (typeof value === 'object') stack.add(value);
      if (typeof other === 'object') stack.add(other);

      try {
        // Handle Date objects
        if (value instanceof Date && other instanceof Date) {
          return value.getTime() === other.getTime();
        }

        // Handle RegExp objects
        if (value instanceof RegExp && other instanceof RegExp) {
          return value.toString() === other.toString();
        }

        // Handle Arrays
        if (Array.isArray(value) && Array.isArray(other)) {
          if (value.length !== other.length) return false;
          return value.every((val, index) =>
            compareWith(val, other[index], customizer, value, other, index, stack),
          );
        }

        // Handle Objects
        if (typeof value === 'object' && typeof other === 'object') {
          if (value.constructor !== other.constructor) return false;
          const keys1 = Object.keys(value);
          const keys2 = Object.keys(other);

          if (keys1.length !== keys2.length) return false;

          return keys1.every(
            (key) =>
              Object.prototype.hasOwnProperty.call(other, key) &&
              compareWith(value[key], other[key], customizer, value, other, key, stack),
          );
        }

        return false;
      } finally {
        // Clean up stack
        if (typeof value === 'object') stack.delete(value);
        if (typeof other === 'object') stack.delete(other);
      }
    }

    return compareWith(value, other, customizer);
  }
}
